import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { backupLogAPI } from '../api/backupLog';
import { toast } from 'react-toastify';
import './BackupLogs.css';

const BackupLogs = () => {
  const { uniqueId } = useParams();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20 });
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchLogs();
  }, [uniqueId, pagination.page, statusFilter]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await backupLogAPI.getLogsByInstallation(
        uniqueId,
        pagination.page,
        pagination.limit,
        statusFilter
      );
      setLogs(response.data);
      setPagination(prev => ({ ...prev, ...response.pagination }));
    } catch (error) {
      toast.error('Failed to fetch backup logs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      success: '✅ Success',
      failed: '❌ Failed',
      in_progress: '⏳ In Progress',
      initiated: '🔄 Initiated',
      partial_success: '⚠️ Partial Success',
      cancelled: '🚫 Cancelled',
    };
    return badges[status] || status;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const formatBytes = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(2)} GB`;
    if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(2)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${bytes} B`;
  };

  if (loading) {
    return <div className="loading">Loading backup logs...</div>;
  }

  return (
    <div className="backup-logs-page">
      <div className="page-header">
        <h1>Backup Logs</h1>
        <div className="filters">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="in_progress">In Progress</option>
            <option value="partial_success">Partial Success</option>
          </select>
        </div>
      </div>

      {logs.length === 0 ? (
        <div className="empty-state">
          <p>No backup logs found</p>
        </div>
      ) : (
        <>
          <div className="logs-table">
            <table>
              <thead>
                <tr>
                  <th>Backup Config</th>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Start Time</th>
                  <th>Duration</th>
                  <th>Size</th>
                  <th>SFTP</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log._id}>
                    <td>
                      <strong>{log.backupConfig?.configName}</strong>
                      {log.scheduleName && (
                        <div className="schedule-info">{log.scheduleName}</div>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge status-${log.status}`}>
                        {getStatusBadge(log.status)}
                      </span>
                    </td>
                    <td>{log.backupType}</td>
                    <td>{new Date(log.startTime).toLocaleString()}</td>
                    <td>{formatDuration(log.duration)}</td>
                    <td>
                      {log.backupFile?.sizeBytes ? formatBytes(log.backupFile.sizeBytes) : 'N/A'}
                    </td>
                    <td>
                      {log.sftpResults?.length > 0 ? (
                        <div className="sftp-summary">
                          {log.sftpResults.filter(r => r.status === 'success').length}/
                          {log.sftpResults.length}
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td>
                      <Link to={`/backup-log/${log._id}`} className="btn btn-small">
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="btn"
              >
                Previous
              </button>
              <span>
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                className="btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BackupLogs;